import { DropdownMenu } from 'radix-ui';
import { ChevronDown } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavItem } from '@/lib/constants/navigation';

export interface NavDropdownProps {
  item: NavItem;
}

/**
 * Desktop navbar dropdown for a parent nav item with children (e.g. "Network"
 * hosting Events / Resources / Trusted Partners, Figma 283:2108). The trigger
 * highlights when any child route is active.
 *
 * `ring-inset` on the focus ring is required, not cosmetic: this trigger lives
 * inside `AppNavbar`'s `overflow-x-auto` nav, and CSS forces `overflow-y` to
 * also become non-`visible` whenever `overflow-x` isn't - so an outward-drawn
 * ring gets its top/bottom clipped by that nav, leaving only two vertical
 * slivers visible (looked like stray "|" pipes either side of the label after
 * clicking/focusing it). Drawing the ring inward keeps it within the button's
 * own box, so it can never be clipped regardless of the ancestor's overflow.
 */
export function NavDropdown({ item }: NavDropdownProps) {
  const { pathname } = useLocation();
  const childActive = (item.children ?? []).some(
    (child) => child.to && pathname.startsWith(child.to),
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={`flex select-none items-center gap-1 whitespace-nowrap text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 ${
            childActive ? 'text-white' : 'text-slate hover:text-white'
          }`}
        >
          {item.label}
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={12}
          className="z-50 min-w-56 rounded-2xl bg-white p-2 shadow-xl"
        >
          {(item.children ?? []).map((child, index) => (
            <div key={child.label}>
              {index > 0 && (
                <DropdownMenu.Separator className="mx-3 h-px bg-divider" />
              )}
              <DropdownMenu.Item asChild>
                <NavLink
                  to={child.to ?? '#'}
                  className="block cursor-pointer rounded-xl px-4 py-3 text-input font-semibold text-navy outline-none transition-colors hover:bg-green hover:text-white focus:bg-green focus:text-white data-highlighted:bg-green data-highlighted:text-white"
                >
                  {child.label}
                </NavLink>
              </DropdownMenu.Item>
            </div>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
